import React, { ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { flatten, omit } from 'lodash';
import { DrawerTypes } from './drawer';

export interface RouteItemCommon {
  name: string;
  icon?: JSX.Element;
  permissions?: string[];
  invisible?: boolean;
  showTitle?: boolean;
}

export interface RouteItemChildWithPath extends RouteItemCommon {
  component: ComponentType<MainContentProps>;
  buttons?: TitleButtonType[];
  exact?: boolean;
  path: string;
}

export interface RouteItemChildWithDrawer extends RouteItemCommon {
  drawer: DrawerTypes;
}

export type RouteItemChild = RouteItemChildWithPath | RouteItemChildWithDrawer;

export interface RouteItemParent extends RouteItemCommon {
  views: RouteItemChild[];
  path?: string;
}

type RouteItemParentWithKey = RouteItemParent & { key: string };

function getFullPath(route: RouteItemChildWithPath, parent: RouteItemParent) {
  const basePath = parent.path || '';
  return `${basePath}${route.path}`;
}

export type IterateRoutesProps = {
  routes: RouteItem[];
  default?: JSX.Element;
  render: (props: {
    key: string;
    route: RouteItemChild;
    parent?: RouteItemParentWithKey;
  }) => JSX.Element | null;
  renderParent?: (props: {
    key: string;
    route: RouteItemParent;
    children: JSX.Element[];
  }) => JSX.Element | null;
};

function IterateRoutes(props: IterateRoutesProps) {
  function recurseFunction(
    routes: RouteItem[],
    parent?: RouteItemParentWithKey,
  ): (JSX.Element | null)[] {
    return flatten(
      routes.map((route, index) => {
        const key = parent ? `${parent.key}.${index}` : `${index}`;

        if ('views' in route) {
          const children = recurseFunction(route.views, { ...route, key }).filter(Boolean);
          if (props.renderParent) {
            return props.renderParent({
              children: children.filter(Boolean) as JSX.Element[],
              route,
              key,
            });
          }
          return children;
        }

        return props.render({
          key,
          parent,
          route: {
            ...route,
            ...('path' in route && {
              path: getFullPath(route, omit(parent, 'key')),
            }),
          },
        });
      }),
    );
  }

  const items = recurseFunction(props.routes).filter(Boolean);
  if (props.default) {
    items.push(props.default);
  }
  return items;
}

export type MapRoutesProps = {
  routes: RouteItem[];
  render: (props: {
    parent?: RouteItemParentWithKey;
    route: RouteItemChildWithPath;
    key: string;
    routeProps: any;
  }) => JSX.Element;
  default?: (props: any) => JSX.Element;
};

function MapRoutes(props: MapRoutesProps) {
  const { routes, render: renderComponent, default: defaultComponent } = props;
  const items = IterateRoutes({
    routes,
    render: ({ route, key, parent }) => {
      return 'path' in route ? (
        <Route
          path={route.path}
          key={key}
          // Component={(routeProps) => renderComponent?.({ parent, key, route, routeProps })}
          element={renderComponent?.({ parent, key, route, routeProps: {} })}
        />
      ) : null;
    },
    // default: props.default && <Route path="*" key="-1" Component={defaultComponent} />,
    default: props.default && (<Route path="*" key="-1" element={defaultComponent ? defaultComponent({}) : null} />),
  });
  return <Routes>{items}</Routes>;
}

export { getFullPath, IterateRoutes, MapRoutes };
